"use client";

import { useState } from "react";
import Button from "@/components/Button";
import LocalizationBanner from "@/components/LocalizationBanner";
import { DEFAULT_LOCALIZATION_BANNER_DATA } from "@/components/LocalizationBanner/constants";
import { SERVICEABILITY_ADDRESS_NR_MASK_PROPS } from "@/constants/newRelicSessionReplay";
import { useAppContext } from "@/context/AppContext";
import fireElementClicked from "@/lib/tagular/fire-element-clicked";
import type { ShoppingFromBannerProps } from "./types";

const LARGE_SHOPPING_FROM_BANNER_CLASSES = {
  section: "bg-neutral-light px-6 py-10 text-neutral-base md:px-8",
  container:
    "mx-auto flex max-w-328 flex-col items-center justify-center gap-5 text-center",
  heading:
    "title-xl-bold max-w-81.75 text-center md:title-2xl-bold md:max-w-full",
  buttonClassName: "min-w-45.5 bg-transparent",
} as const;

const getShoppingFromAddress = (locationDisplay: string): string =>
  locationDisplay.replace(/^Shopping from\s+/i, "");

const ShoppingFromBanner: React.FC<ShoppingFromBannerProps> = ({
  variant = "default",
}) => {
  const { isLoading, localizationType, locationDisplay } = useAppContext();
  const [isChangingAddress, setIsChangingAddress] = useState(false);
  const visible = Boolean(
    locationDisplay &&
      ((variant === "default" &&
        (localizationType === "geo" || localizationType === "full")) ||
        (variant === "large" && localizationType === "full"))
  );

  if (isLoading) {
    return null;
  }

  if (isChangingAddress) {
    return (
      <LocalizationBanner
        {...DEFAULT_LOCALIZATION_BANNER_DATA}
        showWhenLocalized
      />
    );
  }

  if (!visible) {
    return null;
  }

  if (variant === "default") {
    return (
      <section
        className="light px-6 pb-6 text-neutral-base lg:px-16 theme-pad-16b lg:theme-pad-24b xl:theme-pad-24b"
        aria-label="Localized shopping address"
      >
        <p className="text-body2 mt-4 text-center">
          <strong>
            <span data-notranslate="" {...SERVICEABILITY_ADDRESS_NR_MASK_PROPS}>
              {locationDisplay}
            </span>
          </strong>
          <a
            href="/planbuilder?localize=true&drawer=INTERNET"
            onClick={() => {
              fireElementClicked({
                actionOutcome:
                  localizationType === "full"
                    ? "sentToOffers"
                    : "sentToLocalize",
                elementType: "textLink",
                location: "banner",
                text: "Change",
                name: "textLinkShoppingFromBannerChange",
                outboundUrl: "/planbuilder?localize=true&drawer=INTERNET",
              });
            }}
            className="ml-2 text-purple-500 underline"
            title="Change"
          >
            Change
          </a>
        </p>
      </section>
    );
  }

  return (
    <section
      className={LARGE_SHOPPING_FROM_BANNER_CLASSES.section}
      aria-label="Localized shopping address"
    >
      <div className={LARGE_SHOPPING_FROM_BANNER_CLASSES.container}>
        <h2 className={LARGE_SHOPPING_FROM_BANNER_CLASSES.heading}>
          Shopping from{" "}
          <span
            className="text-theme1-base"
            data-notranslate=""
            {...SERVICEABILITY_ADDRESS_NR_MASK_PROPS}
          >
            {getShoppingFromAddress(locationDisplay)}
          </span>
        </h2>
        <Button
          display="outline"
          tone="neutral-dark"
          size="md"
          className={LARGE_SHOPPING_FROM_BANNER_CLASSES.buttonClassName}
          onClick={() => setIsChangingAddress(true)}
        >
          Change address
        </Button>
      </div>
    </section>
  );
};

export default ShoppingFromBanner;
